import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

// A4 proportions; a fixed text width keeps page boundaries stable on resize.
const PAGE_WIDTH = 720;
const PAGE_HEIGHT = 1018;
const GAP = 24;
const STRIDE = PAGE_WIDTH + GAP;

export default function PagedRecordPreview({ children, enabled = true, keyboardEnabled = true }) {
  const host = useRef(null);
  const viewport = useRef(null);
  const [mode, setMode] = useState(1);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(1);
  const [scale, setScale] = useState(1);
  const visiblePages = Math.min(mode, count - page);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const root = host.current;
    const paper = root.querySelector('[aria-label="講義內容預覽"]');
    const measure = () => {
      if (paper) {
        const nextCount = Math.max(1, Math.ceil((paper.scrollWidth + 88) / STRIDE));
        setCount(nextCount);
        setPage((current) => Math.min(current, Math.floor((nextCount - 1) / mode) * mode));
      }
      const width = root.clientWidth;
      if (width) setScale(Math.min(1, width / (mode * STRIDE - GAP)));
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(root);
    if (paper) observer?.observe(paper);
    const mutations = new MutationObserver(measure);
    if (paper) mutations.observe(paper, { subtree: true, childList: true, attributes: true, characterData: true });
    root.addEventListener("load", measure, true);
    window.addEventListener("resize", measure);
    let cancelled = false;
    document.fonts?.ready.then(() => { if (!cancelled) measure(); });
    return () => {
      cancelled = true;
      observer?.disconnect(); mutations.disconnect();
      root.removeEventListener("load", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [enabled, mode, children]);

  useEffect(() => {
    if (!enabled || !keyboardEnabled) return undefined;
    const handleKey = (event) => {
      if (event.key !== "Enter" || event.defaultPrevented || event.isComposing || event.keyCode === 229 || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (target?.closest?.('input, textarea, select, button, a, [contenteditable="true"], [role="textbox"]')) return;
      const dialog = host.current?.closest('[role="dialog"]');
      if (dialog && target?.closest?.('[role="dialog"]') !== dialog) return;
      event.preventDefault();
      setPage((current) => Math.max(0, Math.min(Math.floor((count - 1) / mode) * mode, current + (event.shiftKey ? -mode : mode))));
    };
    window.addEventListener("keydown", handleKey);
    // The parent dialog initially focuses its close button; move into the reader.
    const frame = requestAnimationFrame(() => viewport.current?.focus({ preventScroll: true }));
    return () => { cancelAnimationFrame(frame); window.removeEventListener("keydown", handleKey); };
  }, [enabled, keyboardEnabled, count, mode]);

  if (!enabled) return children;
  const changeMode = (next) => { setMode(next); setPage((current) => Math.floor(current / next) * next); };
  return <Reader ref={host}>
    <Toolbar data-record-export-exclude="true" aria-label="講義預覽控制">
      <div role="group" aria-label="頁面顯示方式">
        <button type="button" aria-pressed={mode === 1} onClick={() => changeMode(1)}>單頁</button>
        <button type="button" aria-pressed={mode === 2} onClick={() => changeMode(2)}>雙頁</button>
      </div>
      <div>
        <button type="button" aria-label="講義上一頁" disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - mode))}>上一頁</button>
        <span role="status" aria-live="polite">第 {page + 1}{visiblePages > 1 ? `–${page + visiblePages}` : ""} 頁 / 共 {count} 頁</span>
        <button type="button" aria-label="講義下一頁" disabled={page + mode >= count} onClick={() => setPage((current) => current + mode)}>下一頁</button>
      </div>
      <small>Enter 下一頁 · Shift + Enter 上一頁</small>
    </Toolbar>
    <Viewport ref={viewport} tabIndex={0} role="region" aria-label="講義分頁閱讀" style={{ width: (mode * STRIDE - GAP) * scale, height: PAGE_HEIGHT * scale }}>
      <SheetWindow style={{ zoom: scale, width: visiblePages * STRIDE - GAP }}>
        <Track style={{ transform: `translateX(-${page * STRIDE}px)` }}>{children}</Track>
      </SheetWindow>
    </Viewport>
  </Reader>;
}

const Reader = styled.div`min-width: 0; width: 100%;`;
const Toolbar = styled.div`
  position: sticky; top: 0; z-index: 4; display: flex; flex-wrap: wrap;
  align-items: center; justify-content: center; gap: 10px 20px; padding: 10px;
  margin-bottom: 16px; border-radius: 8px; background: var(--app-surface, #fff);
  border: 1px solid var(--app-border, #d7dee4); font-size: 13px;
  > div { display: flex; align-items: center; gap: 8px; }
  button { padding: 6px 10px; border-radius: 5px; border: 1px solid var(--app-border, #d7dee4); background: var(--app-surface, #fff); color: inherit; cursor: pointer; }
  button[aria-pressed="true"] { background: #2563eb; border-color: #2563eb; color: white; }
  button:disabled { opacity: .4; cursor: default; }
  small { color: var(--app-muted-text, #687380); }
`;
const Viewport = styled.div`position: relative; margin: 0 auto; max-width: 100%; outline-offset: 4px;`;
const SheetWindow = styled.div`
  height: ${PAGE_HEIGHT}px; overflow: hidden;
  background: repeating-linear-gradient(to right, var(--app-surface, #fff) 0 ${PAGE_WIDTH}px, transparent ${PAGE_WIDTH}px ${STRIDE}px);
  filter: drop-shadow(0 2px 5px #0002);
`;
const Track = styled.div`
  width: ${PAGE_WIDTH}px; padding: 36px 32px; box-sizing: border-box;
  [aria-label="講義內容預覽"] {
    width: 656px; min-height: 0; height: 946px; margin: 0; padding: 0; border: 0;
    background: transparent; column-width: 656px; column-gap: 88px; column-fill: auto;
    overflow: visible;
  }
  [data-record-page-break] { break-before: column; margin: 0; height: 0; border: 0; }
  [data-handout-question-uuid], figure { break-inside: avoid; }
  img { max-height: 880px; object-fit: contain; }
`;
